import React, { useEffect, useId, useRef, useState } from 'react';
import PremarketMascot from './PremarketMascot';
import MarketStateMascot from './MarketStateMascot';

const FamilyMark = ({ mark }) => {
  switch (mark) {
    case 'rise': return <path d="M45 87l5-4 4 2 11-8" />;
    case 'spark': return <path d="M55 76l-5 8h5l-2 8 10-11h-6l2-5" />;
    case 'return': return <><path d="M64 81a10 10 0 1 0-3 11" /><path d="M60 77l5 4-5 3" /></>;
    case 'cross': return <><path d="M45 90l20-12M45 78l20 12" /><circle cx="55" cy="84" r="2" /></>;
    case 'down': return <path d="M45 78l6 5 4-2 10 9m0-6v6h-6" />;
    case 'double-rise': return <><path d="M44 89l6-6 4 2 10-9" /><path d="M48 92l6-4 4 1 7-6" /></>;
    case 'dial': return <><path d="M46 88a10 10 0 0 1 19 0" /><path d="M55 87l5-6" /></>;
    default: return <path d="M49 77h12l5 8-5 8H49l-5-8z" />;
  }
};

// Decorative character. BotLiveActivity provides the accessible, evidence-backed status text.
export default function BotPresence({ kind, motion, connected = false, compact = false, acknowledgementKey = null, resultCue = null, personality, preparationActivity = null }) {
  const gradientPrefix = useId().replace(/:/g, '');
  const [visible, setVisible] = useState(() => typeof document !== 'undefined' && document.visibilityState === 'visible');
  const [acknowledging, setAcknowledging] = useState(false);
  const lastAcknowledgement = useRef(null);
  const [resultMotion, setResultMotion] = useState(null);
  const lastResultCue = useRef(null);

  useEffect(() => {
    const update = () => {
      const isVisible = document.visibilityState === 'visible';
      setVisible(isVisible);
      if (!isVisible) {
        setAcknowledging(false);
        setResultMotion(null);
      }
    };
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  useEffect(() => {
    if (kind !== 'evaluation_started' || !visible) setAcknowledging(false);
  }, [kind, visible]);

  useEffect(() => {
    if (kind !== 'evaluation_started' || !visible || !acknowledgementKey
        || lastAcknowledgement.current === acknowledgementKey) return undefined;
    lastAcknowledgement.current = acknowledgementKey;
    setAcknowledging(true);
    const timeout = window.setTimeout(() => setAcknowledging(false), 1800);
    return () => window.clearTimeout(timeout);
  }, [acknowledgementKey, kind, visible]);

  useEffect(() => {
    if (kind !== 'monitoring' || !visible || !resultCue?.key) {
      setResultMotion(null);
      return undefined;
    }
    if (lastResultCue.current === resultCue.key) return undefined;
    lastResultCue.current = resultCue.key;
    setResultMotion(resultCue.kind);
    const timeout = window.setTimeout(() => setResultMotion(null), 1400);
    return () => window.clearTimeout(timeout);
  }, [kind, resultCue?.key, resultCue?.kind, visible]);

  const sleeping = kind === 'sleeping';
  const waking = kind === 'waking';
  const illustrated = sleeping || kind === 'monitoring' || kind === 'working';
  const awake = ['monitoring', 'standby', 'preparing', 'evaluation_started', 'working', 'replay', 'waking'].includes(kind);
  const disabled = ['paused', 'fault', 'offline', 'archived', 'unavailable', 'inactive'].includes(kind);
  const showOrbit = ['standby', 'preparing', 'replay'].includes(kind);
  // evaluation_started is emitted only with a fresh worker lease. Once the
  // one-off acknowledgement ends, show connected idle breathing, never work.
  const connectedIdle = kind === 'evaluation_started' && visible && !acknowledging;
  return (
    <span className={`bot-presence bot-presence--${kind} bot-presence--family-${personality?.key || 'custom'} ${compact ? 'bot-presence--compact' : ''}`}
      data-presence-state={kind}
      data-presence-family={personality?.key || 'custom'}
      data-presence-variant={personality?.variant ?? 0}
      data-preparation-activity={waking ? preparationActivity || 'none' : 'none'}
      data-presence-motion={motion && visible ? 'on' : 'off'}
      data-presence-connected={connected ? 'true' : 'false'}
      data-presence-idle={connectedIdle ? 'on' : 'off'}
      data-presence-ack={kind === 'evaluation_started' && acknowledging && visible ? 'on' : 'off'}
      data-result-cue={kind === 'monitoring' && visible && resultMotion ? resultMotion : 'off'}
      aria-hidden="true">
      {['monitoring', 'preparing', 'working', 'replay'].includes(kind) && <span className="bot-live-scan" />}
      {waking ? <PremarketMascot activity={preparationActivity} family={personality?.key} />
        : illustrated ? <MarketStateMascot state={sleeping ? 'resting' : 'working'} family={personality?.key} />
          : <svg viewBox="0 0 112 112" focusable="false" role="presentation">
        <defs>
          <linearGradient id={`${gradientPrefix}-shell`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#ffffff" />
            <stop offset="0.56" stopColor="#f8fbff" />
            <stop offset="1" stopColor="#dce7f3" />
          </linearGradient>
          <linearGradient id={`${gradientPrefix}-screen`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="color-mix(in srgb, var(--presence-body) 48%, white)" />
            <stop offset="0.52" stopColor="color-mix(in srgb, var(--presence-body) 67%, white)" />
            <stop offset="1" stopColor="color-mix(in srgb, var(--presence-body) 88%, white)" />
          </linearGradient>
          <linearGradient id={`${gradientPrefix}-jacket`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="color-mix(in srgb, var(--presence-body) 60%, white)" />
            <stop offset="0.52" stopColor="var(--presence-body)" />
            <stop offset="1" stopColor="var(--presence-face)" />
          </linearGradient>
        </defs>
        <ellipse className="bot-presence-ground" cx="56" cy="99" rx="28" ry="5" />
        <circle className="bot-presence-glow" cx="54" cy="60" r="34" />
        {showOrbit && <g className="bot-presence-orbit">
          <ellipse className="bot-presence-orbit-path" cx="56" cy="57" rx="45" ry="42" />
          <circle className="bot-presence-orbit-node" cx="101" cy="57" r="3" />
        </g>}
        <g className="bot-presence-character">
          <path className="bot-presence-antenna" d="M56 16V10" />
          {personality?.variant === 1 ? <rect className="bot-presence-antenna-tip" x="53" y="4" width="6" height="6" rx="2" />
            : personality?.variant === 2 ? <path className="bot-presence-antenna-tip" d="M56 3l4 4-4 4-4-4z" />
              : <circle className="bot-presence-antenna-tip" cx="56" cy="7" r="3.5" />}
          <path className="bot-presence-arm" d="M37 77Q30 73 25 79L19 88Q18 94 24 95Q30 96 33 89L39 84Z" fill={`url(#${gradientPrefix}-shell)`} />
          <path className="bot-presence-arm" d="M75 77Q82 73 87 79L93 88Q94 94 88 95Q82 96 79 89L73 84Z" fill={`url(#${gradientPrefix}-shell)`} />
          <circle className="bot-presence-mitt" cx="23" cy="91" r="5" fill={`url(#${gradientPrefix}-jacket)`} />
          <circle className="bot-presence-mitt" cx="89" cy="91" r="5" fill={`url(#${gradientPrefix}-jacket)`} />
          <path className="bot-presence-body" d="M40 70Q56 66 72 70Q81 75 81 88V94Q77 91 72 97Q67 101 61 96Q56 101 51 96Q45 101 40 97Q35 91 31 94V88Q31 75 40 70Z" fill={`url(#${gradientPrefix}-jacket)`} />
          <path className="bot-presence-belly" d="M33 90Q56 97 79 90V95Q73 95 70 100Q63 97 56 101Q49 97 42 100Q39 95 33 95Z" fill={`url(#${gradientPrefix}-shell)`} />
          <ellipse className="bot-presence-foot" cx="45" cy="101" rx="9" ry="3.5" fill={`url(#${gradientPrefix}-jacket)`} />
          <ellipse className="bot-presence-foot" cx="67" cy="101" rx="9" ry="3.5" fill={`url(#${gradientPrefix}-jacket)`} />
          <rect className="bot-presence-ear" x="9" y="38" width="15" height="28" rx="7.5" fill={`url(#${gradientPrefix}-jacket)`} />
          <rect className="bot-presence-ear" x="88" y="38" width="15" height="28" rx="7.5" fill={`url(#${gradientPrefix}-jacket)`} />
          <path className="bot-presence-ear-glint" d="M14 43v15M98 43v15" />
          <rect className="bot-presence-head" x="16" y="16" width="80" height="60" rx="27" fill={`url(#${gradientPrefix}-shell)`} />
          <path className="bot-presence-shell-shine" d="M23 38Q26 22 43 21H72Q88 22 91 38" />
          <rect className="bot-presence-face" x="24" y="24" width="64" height="43" rx="19" fill={`url(#${gradientPrefix}-screen)`} />
          <path className="bot-presence-screen-glint" d="M30 34Q37 27 51 27H70" />
          {awake ? <g className="bot-presence-open-eyes">
            {[42, 70].map((x) => <g key={x}>
              <ellipse cx={x + (personality?.expression === 'eager' ? 1 : 0)} cy="46" rx={personality?.expression === 'focused' ? 5 : 4.6} ry={personality?.expression === 'soft' ? 6.5 : 8} fill="var(--presence-face)" />
              <ellipse cx={x - 1.2} cy="42.5" rx="1.6" ry="2" fill="white" />
              <circle cx={x - 2.5} cy="49" r="0.9" fill="white" opacity="0.76" />
            </g>)}
          </g> : <g className="bot-presence-neutral-eyes" fill="var(--presence-face)">
            <rect x="36" y="45" width="12" height="2.5" rx="1.25" />
            <rect x="64" y="45" width="12" height="2.5" rx="1.25" />
          </g>}
          {!disabled && <path className="bot-presence-mouth" d="M50 56Q56 62 62 56" />}
          <rect className="bot-presence-chest" x="43" y="78" width="26" height="16" rx="8" />
          <g className="bot-presence-family-mark" aria-hidden="true"><FamilyMark mark={personality?.mark} /></g>
        </g>
        {showOrbit && <g className="bot-presence-bars">
          <rect className="bot-presence-bar bot-presence-bar--one" x="91" y="82" width="3" height="7" rx="1.5" />
          <rect className="bot-presence-bar bot-presence-bar--two" x="96" y="78" width="3" height="11" rx="1.5" />
          <rect className="bot-presence-bar bot-presence-bar--three" x="101" y="74" width="3" height="15" rx="1.5" />
        </g>}
        {disabled && <g className="bot-presence-state-mark">
          <circle cx="92" cy="89" r="12" />
          {kind === 'paused' ? <g><rect x="87" y="83" width="3" height="12" rx="1" /><rect x="94" y="83" width="3" height="12" rx="1" /></g>
            : kind === 'fault' ? <text x="89" y="95" fontSize="17" fontWeight="900">!</text>
              : kind === 'offline' ? <path d="M86 83l12 12M98 83L86 95" strokeWidth="2.5" />
                : <path d="M87 89h10" strokeWidth="2.5" />}
        </g>}
      </svg>}
      {illustrated && <svg className="bot-presence-family-badge" viewBox="40 70 32 28" focusable="false" role="presentation">
        <g className="bot-presence-family-mark"><FamilyMark mark={personality?.mark} /></g>
      </svg>}
      {connected && <span className="bot-presence-indicator" />}
    </span>
  );
}
